import { DataSTag, Sel, STagNew } from '#~/stag/index.js';
import { cn } from '@studio/design-system/motion';
import { dayjs } from '@studio/js-utils/time';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { clsx } from 'cn';
import { useEffect, useMemo, useState } from 'react';
import { twMerge } from 'cn';
import { Button } from '@studio/design-system/button';
import { Ellipses } from '@studio/design-system/label';

interface Props {
  type: 'audio' | 'video';
  name: string;
  config?: {
    name: string;
    value?: T_Maybe<React.ReactNode>;
    duration?: T_Maybe<number> | string;
  }[];
  error?: string | undefined;
  isActive: boolean;
  selected: boolean;
  onSelection: () => void;
  handleStopStreaming?: () => void;
  isSelectable?: boolean;
  className?: string;
  isStopOperationInProgress?: boolean;
  destinationTitle?: string;
  hideIcon?: boolean;
  /** Device id used as the STag uniqueID so tests can address one card. */
  stagId?: string | undefined;
  /** When set, the card cannot be selected; the text is shown inline and
   *  `onBlockedSelection` fires on click instead of `onSelection`. */
  unselectableReason?: string | undefined;
  onBlockedSelection?: (() => void) | undefined;
  /** When given together with `error`, the error block renders a dismiss control. */
  onDismissError?: (() => void) | undefined;
}

export const SourceCard = ({
  type,
  name: sourceName,
  error,
  selected,
  isActive,
  config,
  onSelection,
  handleStopStreaming,
  isSelectable = true,
  className,
  isStopOperationInProgress = false,
  destinationTitle,
  hideIcon = false,
  stagId,
  unselectableReason,
  onBlockedSelection,
  onDismissError
}: Props) => {
  const [includeBuiltin, setIncludeBuiltin] = useState(false);

  const divider = <div className='mx-12 border-t-1 border-base-50' />;
  const displayError = unselectableReason ?? error;
  const isBlocked = Boolean(unselectableReason);
  // Proactive codec reason is the state of the source; only backend errors dismiss.
  const canDismiss = Boolean(error && onDismissError && !unselectableReason);

  const handleActivate = () => {
    if (isBlocked) {
      onBlockedSelection?.();
      return;
    }
    if (isSelectable) onSelection();
  };

  useMemo(
    () =>
      type === 'video' && (
        <div
          className={clsx('mx-12 flex cursor-pointer items-center gap-6 py-12', {
            'pointer-events-none opacity-50': isActive
          })}
          onClick={() => setIncludeBuiltin(!includeBuiltin)}
        >
          {includeBuiltin ? (
            <div className='flex h-16 w-16 items-center justify-center rounded bg-primary'>
              <i className={cn('i-mdi-check-bold text-white')} />
            </div>
          ) : (
            <div className='flex h-16 w-16 items-center justify-center rounded border-1 border-foreground'></div>
          )}
          <span className='text-muted'>Include built-in audio with output.</span>
        </div>
      ),
    []
  );

  return (
    <div
      className={twMerge(
        className,
        'rounded border-1 bg-base-100',
        displayError ? 'border-error' : 'border-transparent'
      )}
    >
      <button
        type='button'
        {...STagNew(Sel.ProductionSourceCard, 'select', stagId)}
        onClick={handleActivate}
        // Per rule: keyboard-reachable-interactive-elements (ai.knowledge/global) —
        // aria-disabled keeps the card in the tab order so the click can explain why.
        aria-disabled={isBlocked ? 'true' : undefined}
        className={cn(
          'group flex w-full items-center gap-12 p-12 text-left',
          isBlocked && 'cursor-not-allowed text-muted'
        )}
        disabled={!isSelectable}
      >
        <div
          className={cn(
            'flex grow items-center justify-between gap-4 transition-colors duration-250',
            {
              'text-muted': !selected
            }
          )}
        >
          <div className='flex items-center gap-12'>
            <i
              className={clsx('text-lg', {
                'text-error': isActive,
                'i-lucide-video': type === 'video',
                'i-lucide-volume-1': type === 'audio'
              })}
            />
            <div className='flex flex-col'>
              {destinationTitle ? (
                <>
                  <Ellipses
                    className='mb-4 max-w-[200px] text-md font-medium text-light'
                    title={destinationTitle}
                  >
                    {destinationTitle}
                  </Ellipses>
                  <Ellipses className='max-w-[200px] text-sm text-primary' title={sourceName}>
                    {sourceName}
                  </Ellipses>
                </>
              ) : (
                <Ellipses
                  className='max-w-[200px] text-md font-regular text-primary'
                  title={sourceName}
                >
                  {sourceName}
                </Ellipses>
              )}
            </div>
          </div>

          {isActive && handleStopStreaming && (
            <Button
              variant='dangerSecondary'
              size='lg'
              disabled={!isActive || isStopOperationInProgress}
              onClick={(event) => {
                event.stopPropagation();
                handleStopStreaming();
              }}
            >
              {isStopOperationInProgress ? 'Stopping...' : 'Stop Streaming'}
            </Button>
          )}
        </div>
        {isSelectable && !hideIcon && !isBlocked && (
          <div className='shrink-0'>
            <span
              className={clsx(
                'flex h-24 w-24 items-center justify-center rounded-full border-1 transition-[all] duration-[200]',
                {
                  'border-white': !selected,
                  'border-transparent bg-primary': selected
                }
              )}
            >
              <i
                className={clsx(
                  'i-mdi-check-bold text-[16px] transition-opacity group-hover:opacity-75',
                  {
                    'opacity-0': !selected,
                    'opacity-100': selected
                  }
                )}
              />
            </span>
          </div>
        )}
      </button>
      {displayError && (
        <>
          {divider}
          <div className='m-12' {...DataSTag(Sel.ProductionSourceCard, 'error', stagId)}>
            <p className='flex items-center gap-6 rounded-4 border-1 border-error p-12'>
              <i className='i-ion-warning-outline text-xl text-error' />
              <span className='grow'>{displayError}</span>
              {canDismiss && (
                <button
                  type='button'
                  {...STagNew(Sel.ProductionSourceCard, 'dismiss_error', stagId)}
                  aria-label='Dismiss error'
                  className='shrink-0 text-muted hover:text-foreground'
                  onClick={(event) => {
                    event.stopPropagation();
                    onDismissError?.();
                  }}
                >
                  <i className='i-lucide-x text-lg' />
                </button>
              )}
            </p>
          </div>
        </>
      )}
      {isActive && config && (
        <>
          {divider}
          <div className='p-12'>
            <div className='flex rounded border-1 border-base-50 bg-base-200'>
              {(config ?? []).map(({ name, value, duration }, i) => (
                <div
                  key={name}
                  className={clsx('flex flex-1 flex-col gap-6 p-12', {
                    'border-l-1 border-base-50': i !== 0
                  })}
                >
                  <div className='text-sm text-secondary'>{name}</div>
                  {duration ? (
                    <div className='flex-shrink-0 whitespace-nowrap text-muted'>
                      <DurationTimer duration={duration} />
                    </div>
                  ) : (
                    <div className='flex-shrink-0 whitespace-nowrap text-muted'>{value}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const DurationTimer = ({ duration }: { duration: number | string }) => {
  if (typeof duration === 'string') {
    return <>{duration}</>;
  }

  const projectWithRef = (duration: number, refDate: Date) => {
    const diff = +new Date() - +refDate;

    return duration + diff / 1000;
  };

  const [refTime, setRefTime] = useState(duration);
  const [refDate, setRefDate] = useState(new Date());
  const [timerCounter, setTimerCounter] = useState(0);
  const [lastTime, setLastTime] = useState(duration);

  const formatTime = (duration: number) => {
    const dur = dayjs.duration(duration ?? 0, 'seconds');
    const durationFormat = [
      `${dur.hours().toString().padStart(2, '0')}`,
      `${dur.minutes().toString().padStart(2, '0')}`,
      `${dur.seconds().toString().padStart(2, '0')}`
    ].join(':');

    return durationFormat;
  };

  useEffect(() => {
    if (lastTime !== duration) {
      setLastTime(duration);

      const currProj = projectWithRef(refTime, refDate);

      const drift = Math.abs(duration - currProj);

      if (drift > 5) {
        setRefTime(duration);
        setRefDate(new Date());
      }
    }
  }, [duration]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimerCounter(timerCounter + 1);
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  });

  const projTime = projectWithRef(refTime, refDate);

  return <>{formatTime(projTime)}</>;
};
